import { DEFAULT_PREFIX_PHONE } from '@/constants';

import { PHONE_COUNTRY_CONFIG } from './phoneCountryConfig';

export interface ExtractedPhone {
  prefix: string;
  number: string;
}

/**
 * Extracts the phone prefix and number from a full phone string.
 * Tries to match against known country prefixes from the config.
 * Falls back to DEFAULT_PREFIX_PHONE (+569) if no valid prefix found.
 *
 * @param fullPhone - The complete phone number with prefix (e.g., "+59112341234567")
 * @returns Object with separated prefix and number
 *
 * @example
 * extractPhonePrefix("+59112341234567") // { prefix: "+591", number: "12341234567" }
 * extractPhonePrefix("+56912345678") // { prefix: "+569", number: "12345678" }
 * extractPhonePrefix("12345678") // { prefix: "+569", number: "12345678" }
 */
export const extractPhonePrefix = (fullPhone: string): ExtractedPhone => {
  if (!fullPhone) {
    return {
      prefix: DEFAULT_PREFIX_PHONE,
      number: ''
    };
  }

  const cleanPhone = fullPhone.replace(/\s/g, '').trim();

  // * If phone doesn't start with +, assume it's a Chilean number without prefix
  if (!cleanPhone.startsWith('+')) {
    return {
      prefix: DEFAULT_PREFIX_PHONE,
      number: cleanPhone
    };
  }

  // *Sort prefixes by length (longest first) to match 3-digit prefixes before 2-digit
  const sortedPrefixes = Object.keys(PHONE_COUNTRY_CONFIG).sort(
    (a, b) => b.length - a.length
  );

  // Try to match against known prefixes
  for (const prefix of sortedPrefixes) {
    if (cleanPhone.startsWith(prefix)) {
      const number = cleanPhone.substring(prefix.length);
      return {
        prefix,
        number
      };
    }
  }

  // * If no match found, try to extract first 2-4 characters as prefix
  // * This handles edge cases where prefix might not be in our config
  const prefixMatch = cleanPhone.match(/^\+(\d{1,3})/);
  if (prefixMatch) {
    const extractedPrefix = prefixMatch[0];
    const number = cleanPhone.substring(extractedPrefix.length);
    return {
      prefix: extractedPrefix,
      number
    };
  }

  // * Last resort: default to Chilean prefix
  return {
    prefix: DEFAULT_PREFIX_PHONE,
    number: cleanPhone.replace(/^\+/, '')
  };
};
